import "@/App.css";
import "./i18n/config";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute, PublicRoute, StatusRoute } from "./components/ProtectedRoute";
import { PendingGate } from "./components/UnlockGate";
import { Toaster } from "./components/ui/sonner";
import { fbqTrack } from "./lib/fbpixel";

function PixelRouteTracker() {
    const location = useLocation();
    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return; } // initial PageView fired by index.html
        fbqTrack('PageView');
    }, [location.pathname]);
    return null;
}

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import StudentDashboard from "./pages/StudentDashboard";
import CoursesPage from "./pages/CoursesPage";
import CourseDetail from "./pages/CourseDetail";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCourses from "./pages/AdminCourses";
import AdminStudents from "./pages/AdminStudents";
import AdminSignals from "./pages/AdminSignals";
import AdminEngine from "./pages/AdminEngine";
import StudentSignals from "./pages/StudentSignals";
import RiskCalculator from "./pages/RiskCalculator";
import Assistant from "./pages/Assistant";
import ChallengePage from "./pages/ChallengePage";
import AdminChallenges from "./pages/AdminChallenges";
import AdminReviews from "./pages/AdminReviews";
import AdminLeads from "./pages/AdminLeads";
import PendingApproval from "./pages/PendingApproval";
import Rejected from "./pages/Rejected";
import LearnPage from "./pages/LearnPage";
import { AboutUs, ContactUs, DisclaimerPage } from "./pages/CompliancePages";
import AdminAnalyst from "./pages/AdminAnalyst";
import AdminCaseStudies from "./pages/AdminCaseStudies";

function App() {
    return (
        <AuthProvider>
            <div className="App">
                <BrowserRouter>
                    <PixelRouteTracker />
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/learn" element={<LearnPage />} />
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route path="/contact-us" element={<ContactUs />} />
                        <Route path="/disclaimer" element={<DisclaimerPage />} />
                        <Route
                            path="/login"
                            element={
                                <PublicRoute>
                                    <Login />
                                </PublicRoute>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <PublicRoute>
                                    <Register />
                                </PublicRoute>
                            }
                        />

                        {/* Status Routes */}
                        <Route
                            path="/pending-approval"
                            element={
                                <StatusRoute status="pending">
                                    <PendingApproval />
                                </StatusRoute>
                            }
                        />
                        <Route
                            path="/rejected"
                            element={
                                <StatusRoute status="rejected">
                                    <Rejected />
                                </StatusRoute>
                            }
                        />

                        {/* Student Routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <StudentDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/courses"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="courses"><CoursesPage /></PendingGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/courses/:courseId"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="course"><CourseDetail /></PendingGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/signals"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="signals"><StudentSignals /></PendingGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tools/risk-calculator"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="riskCalc"><RiskCalculator /></PendingGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/assistant"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="assistant"><Assistant /></PendingGate>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/challenge"
                            element={
                                <ProtectedRoute>
                                    <PendingGate featureKey="challenge"><ChallengePage /></PendingGate>
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin Routes */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/courses"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminCourses />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/students"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminStudents />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/signals"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminSignals />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/engine"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminEngine />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/challenges"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminChallenges />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/reviews"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminReviews />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/admin/analyst" element={<ProtectedRoute requireAdmin><AdminAnalyst /></ProtectedRoute>} />
                        <Route path="/admin/case-studies" element={<ProtectedRoute requireAdmin><AdminCaseStudies /></ProtectedRoute>} />
                        <Route
                            path="/admin/leads"
                            element={
                                <ProtectedRoute requireAdmin>
                                    <AdminLeads />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
            </div>
        </AuthProvider>
    );
}

export default App;
